import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Send, Heart, Shield } from "lucide-react";

interface DonationFormData {
  donor_name: string;
  email: string;
  phone: string;
  amount: string;
  donation_frequency: string;
  message: string;
  is_anonymous: boolean;
  preferred_contact: string;
}

const presetAmounts = [25, 50, 100, 250, 500];

export const DonationForm = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState<DonationFormData>({
    donor_name: "",
    email: "",
    phone: "",
    amount: "",
    donation_frequency: "one-time",
    message: "",
    is_anonymous: false,
    preferred_contact: "email"
  });

  const handleInputChange = (field: keyof DonationFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getUtmData = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      source: urlParams.get('utm_source') || 'direct',
      medium: urlParams.get('utm_medium') || 'website',
      campaign: urlParams.get('utm_campaign') || 'individual-donation',
      term: urlParams.get('utm_term') || null,
      content: urlParams.get('utm_content') || null,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.amount || parseFloat(formData.amount) < 5) {
      toast({
        title: "Missing Information",
        description: "Please provide an email and donation amount (minimum $5 KYD)",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.functions.invoke('sponsor-inquiry', {
        body: {
          company: null, // Individual donation, no company
          contact_name: formData.donor_name || "Anonymous Donor",
          donor_name: formData.donor_name,
          email: formData.email,
          phone: formData.phone || null,
          tier_id: null, // No tier for individual donations
          amount: parseFloat(formData.amount),
          donation_frequency: formData.donation_frequency,
          message: formData.message || null,
          support_type: 'individual',
          is_anonymous: formData.is_anonymous,
          preferred_contact: formData.preferred_contact,
          utm_data: getUtmData()
        }
      });

      if (error) throw error;

      toast({
        title: "Donation Inquiry Submitted!",
        description: "Thank you for your generosity. We'll be in touch within 1-2 business days with donation instructions.",
      });

      // Reset form
      setFormData({
        donor_name: "",
        email: "",
        phone: "",
        amount: "",
        donation_frequency: "one-time",
        message: "",
        is_anonymous: false,
        preferred_contact: "email"
      });

    } catch (error: any) {
      console.error('Error submitting donation inquiry:', error);
      toast({
        title: "Submission Error",
        description: "Please try again or contact us directly at contact@boboandteedee.com",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="donation-form" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Heart className="h-16 w-16 mx-auto mb-6 text-primary" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Make a Donation
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Ready to support our mission? Fill out the form below and we'll contact you 
            within 1-2 business days with secure donation instructions.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center bg-gradient-to-r from-primary/10 to-secondary/10">
              <CardTitle className="text-2xl">Donation Details</CardTitle>
              <p className="text-muted-foreground flex items-center justify-center gap-2">
                <Shield className="h-4 w-4" />
                Secure and confidential
              </p>
            </CardHeader>
            
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="amount">Donation Amount (KYD) *</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2 mb-4">
                    {presetAmounts.map((amount) => (
                      <Button
                        key={amount}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleInputChange('amount', amount.toString())}
                        className={formData.amount === amount.toString() ? 'bg-primary text-primary-foreground' : ''}
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>
                  <Input
                    id="donation-amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="Enter custom amount"
                    min="5"
                    step="5"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="donation_frequency">Donation Frequency</Label>
                  <Select onValueChange={(value) => handleInputChange('donation_frequency', value)} defaultValue="one-time">
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one-time">One-time Donation</SelectItem>
                      <SelectItem value="monthly">Monthly Recurring</SelectItem>
                      <SelectItem value="quarterly">Quarterly Recurring</SelectItem>
                      <SelectItem value="annual">Annual Recurring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="donor_name">Your Name</Label>
                    <Input
                      id="donor_name"
                      type="text"
                      value={formData.donor_name}
                      onChange={(e) => handleInputChange('donor_name', e.target.value)}
                      placeholder="Your full name (optional)"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+1 (345) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="preferred_contact">Preferred Contact</Label>
                    <Select onValueChange={(value) => handleInputChange('preferred_contact', value)} defaultValue="email">
                      <SelectTrigger>
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="mail">Regular Mail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Share why you're supporting Bobo & Teedee® (optional)..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="anonymous" 
                    checked={formData.is_anonymous}
                    onCheckedChange={(checked) => handleInputChange('is_anonymous', checked as boolean)}
                  />
                  <Label htmlFor="anonymous" className="text-sm">
                    Make this donation anonymous (your name won't be shared publicly)
                  </Label>
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full text-lg py-6"
                    disabled={loading}
                  >
                    {loading ? (
                      "Processing..."
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Submit Donation Request
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-center text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Shield className="h-4 w-4" />
                    <span className="font-semibold">Your Privacy is Protected</span>
                  </div>
                  <p>
                    We respect your privacy and will never share your information. 
                    Donations are processed securely through our banking partner, Sands Ltd.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};